import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, ShieldCheck, ShieldX, Truck, Search, X, Trash2, Printer, PackageCheck, SearchCheck, ArrowRight, FileText } from "lucide-react";

const STATUSES = {
  received: { label: "Primita", color: "bg-blue-500", text: "text-blue-400", border: "border-blue-500" },
  in_verification: { label: "In verificare", color: "bg-amber-500", text: "text-amber-400", border: "border-amber-500" },
  repaired: { label: "Reparata pe garantie", color: "bg-green-500", text: "text-green-400", border: "border-green-500" },
  cancelled: { label: "Garantie ANULATA", color: "bg-red-600", text: "text-red-400", border: "border-red-600" },
  shipped: { label: "Expediata inapoi", color: "bg-purple-500", text: "text-purple-400", border: "border-purple-500" },
};

const CANCEL_REASONS = [
  "Contact cu apa / lichide",
  "Lovita / cazuta / accident",
  "Interventie neautorizata",
  "Uzura normala (nu e defect de fabricatie)",
  "Sigiliu rupt / componente lipsa",
  "Utilizare necorespunzatoare (supraincarcare, off-road)",
  "Alt motiv (detaliaza in constatare)",
];

const emptyForm = { awb_in: "", customer_name: "", customer_phone: "", scooter_model: "", serial_number: "", issue_description: "", notify_sms: true };

export default function AdminWarranties() {
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verdictTarget, setVerdictTarget] = useState(null);
  const [verdict, setVerdict] = useState({ type: "repaired", cancel_reason: "", verdict_notes: "", resolution: "" });
  const [shipTarget, setShipTarget] = useState(null);
  const [awbOut, setAwbOut] = useState("");

  const load = async () => {
    const r = await api.get("/admin/warranties");
    setItems(r.data);
  };
  useEffect(() => {
    load();
    api.get("/models").then((r) => setModels(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.awb_in || !form.customer_name || !form.scooter_model || !form.issue_description) {
      toast.error("Completeaza AWB, nume, model si problema"); return;
    }
    try {
      await api.post("/admin/warranties", form);
      toast.success("Garantie inregistrata!");
      setForm(emptyForm);
      setOpen(false);
      load();
    } catch { toast.error("Eroare la salvare"); }
  };

  const setStatus = async (id, status, extra = {}) => {
    try {
      await api.patch(`/admin/warranties/${id}`, { status, ...extra });
      toast.success("Status actualizat");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eroare"); }
  };

  const saveVerdict = async (e) => {
    e.preventDefault();
    if (verdict.type === "cancelled" && !verdict.cancel_reason) { toast.error("Alege motivul anularii"); return; }
    await setStatus(verdictTarget.id, verdict.type, {
      cancel_reason: verdict.type === "cancelled" ? verdict.cancel_reason : "",
      verdict_notes: verdict.verdict_notes,
      resolution: verdict.resolution,
    });
    setVerdictTarget(null);
    setVerdict({ type: "repaired", cancel_reason: "", verdict_notes: "", resolution: "" });
  };

  const saveShip = async (e) => {
    e.preventDefault();
    if (!awbOut.trim()) { toast.error("Introdu AWB-ul de retur"); return; }
    await setStatus(shipTarget.id, "shipped", { awb_out: awbOut.trim() });
    setShipTarget(null);
    setAwbOut("");
  };

  const remove = async (id) => {
    if (!confirm("Sterge garantia?")) return;
    await api.delete(`/admin/warranties/${id}`);
    toast.success("Garantie stearsa");
    load();
  };

  const filtered = items.filter((w) => {
    if (statusFilter !== "all" && w.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [w.warranty_number, w.awb_in, w.awb_out, w.customer_name, w.customer_phone, w.scooter_model, w.serial_number]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  const counts = items.reduce((acc, w) => { acc[w.status] = (acc[w.status] || 0) + 1; return acc; }, {});
  const active = items.filter((w) => w.status !== "shipped").length;

  return (
    <div data-testid="admin-warranties-page">
      {/* Header cu branding partener */}
      <div className="relative overflow-hidden border border-neutral-800 bg-gradient-to-r from-neutral-950 via-black to-neutral-950 p-6 mb-8">
        <div className="absolute -right-8 -top-8 w-48 h-48 bg-[#00E5FF]/10 rounded-full blur-2xl" />
        <div className="flex flex-wrap items-center justify-between gap-4 relative">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#00E5FF] flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="font-display text-3xl uppercase leading-none">Garantii</h1>
              <div className="text-xs uppercase tracking-[0.25em] mt-1.5">
                <span className="text-neutral-500">Partener oficial · </span>
                <span className="text-[#00E5FF] font-bold">trotinete-ieftine.ro</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right mr-2">
              <div className="text-2xl font-bold leading-none">{active}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">In service acum</div>
            </div>
            <Button data-testid="new-warranty-btn" onClick={() => setOpen(true)} className="bg-[#00E5FF] text-black hover:bg-[#5CEEFF] rounded-none uppercase tracking-wider font-bold">
              <Plus className="w-4 h-4 mr-2" /> Garantie noua
            </Button>
          </div>
        </div>
      </div>

      {/* Stat pills pe status */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {Object.entries(STATUSES).map(([key, s]) => (
          <button key={key} data-testid={`warranty-stat-${key}`}
            onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
            className={`border p-3 text-left transition-colors ${statusFilter === key ? `${s.border} bg-neutral-900` : "border-neutral-800 hover:border-neutral-600"}`}>
            <div className={`text-2xl font-bold ${s.text}`}>{counts[key] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
        <Input data-testid="warranty-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Cauta AWB, nume, model, serie..."
          className="pl-9 bg-neutral-950 border-neutral-800 rounded-none" />
        {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"><X className="w-4 h-4" /></button>}
      </div>

      {/* Lista */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="border border-dashed border-neutral-800 p-12 text-center text-neutral-500">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-neutral-700" />
            Nicio garantie {statusFilter !== "all" ? "cu acest status" : "inregistrata"}.
          </div>
        )}
        {filtered.map((w) => {
          const s = STATUSES[w.status] || STATUSES.received;
          return (
            <div key={w.id} data-testid={`warranty-card-${w.warranty_number}`} className={`border border-neutral-800 bg-neutral-950 border-l-4 ${s.border}`}>
              <div className="p-4 flex flex-wrap items-start gap-4">
                <div className="min-w-[110px]">
                  <div className="font-mono font-bold text-[#00E5FF]">{w.warranty_number}</div>
                  <span className={`inline-block mt-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold text-black ${s.color}`}>{s.label}</span>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-lg leading-tight">{w.customer_name}</div>
                  <div className="text-sm text-neutral-400">{w.scooter_model}{w.serial_number ? ` · SN: ${w.serial_number}` : ""}</div>
                  {w.customer_phone && <div className="text-xs text-neutral-500 mt-0.5">Tel: {w.customer_phone}</div>}
                  <div className="text-sm text-neutral-300 mt-2 line-clamp-2">{w.issue_description}</div>
                  {w.cancel_reason && (
                    <div className="mt-2 inline-flex items-center gap-2 bg-red-950/60 border border-red-900 text-red-300 text-xs px-2 py-1">
                      <ShieldX className="w-3.5 h-3.5" /> Motiv anulare: {w.cancel_reason}
                    </div>
                  )}
                  {w.verdict_notes && <div className="text-xs text-neutral-500 mt-1.5 italic">Constatare: {w.verdict_notes}</div>}
                  {w.resolution && <div className="text-xs text-green-500/80 mt-1 italic">Rezolvare: {w.resolution}</div>}
                </div>
                <div className="min-w-[180px] text-sm space-y-1.5">
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-neutral-500 text-xs uppercase">AWB intrare:</span>
                    <span className="font-mono font-medium">{w.awb_in}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-3.5 h-3.5 text-purple-400" />
                    <span className="text-neutral-500 text-xs uppercase">AWB retur:</span>
                    <span className="font-mono font-medium">{w.awb_out || "—"}</span>
                  </div>
                  <div className="text-xs text-neutral-500">Intrata: {new Date(w.received_at).toLocaleDateString("ro-RO")}</div>
                  {w.shipped_at && <div className="text-xs text-neutral-500">Expediata: {new Date(w.shipped_at).toLocaleDateString("ro-RO")}</div>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 px-4 pb-3 border-t border-neutral-900 pt-3">
                {w.status === "received" && (
                  <Button size="sm" data-testid={`verify-btn-${w.warranty_number}`} onClick={() => setStatus(w.id, "in_verification")}
                    className="bg-amber-500 text-black hover:bg-amber-400 rounded-none uppercase text-xs tracking-wider font-bold">
                    <SearchCheck className="w-3.5 h-3.5 mr-1.5" /> Incepe verificarea
                  </Button>
                )}
                {(w.status === "received" || w.status === "in_verification") && (
                  <Button size="sm" data-testid={`verdict-btn-${w.warranty_number}`} onClick={() => { setVerdictTarget(w); setVerdict({ type: "repaired", cancel_reason: "", verdict_notes: w.verdict_notes || "", resolution: w.resolution || "" }); }}
                    className="bg-white text-black hover:bg-neutral-200 rounded-none uppercase text-xs tracking-wider font-bold">
                    <PackageCheck className="w-3.5 h-3.5 mr-1.5" /> Verdict
                  </Button>
                )}
                {(w.status === "repaired" || w.status === "cancelled") && (
                  <Button size="sm" data-testid={`ship-btn-${w.warranty_number}`} onClick={() => { setShipTarget(w); setAwbOut(w.awb_out || ""); }}
                    className="bg-purple-500 text-black hover:bg-purple-400 rounded-none uppercase text-xs tracking-wider font-bold">
                    <Truck className="w-3.5 h-3.5 mr-1.5" /> Expediaza inapoi
                  </Button>
                )}
                {(w.status === "repaired" || w.status === "cancelled" || w.status === "shipped") && (
                  <Button size="sm" data-testid={`box-sheet-btn-${w.warranty_number}`} onClick={() => window.open(`/admin/garantii/${w.id}/colet`, "_blank")}
                    className="bg-neutral-800 text-white hover:bg-neutral-700 rounded-none uppercase text-xs tracking-wider font-bold border border-neutral-700">
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> Foaie colet
                  </Button>
                )}
                <button data-testid={`print-warranty-${w.warranty_number}`} onClick={() => window.open(`/admin/garantii/${w.id}/print`, "_blank")}
                  className="ml-auto p-2 text-neutral-400 hover:text-[#00E5FF]" title="Printeaza fisa">
                  <Printer className="w-4 h-4" />
                </button>
                <button data-testid={`delete-warranty-${w.warranty_number}`} onClick={() => remove(w.id)} className="p-2 text-neutral-500 hover:text-red-500" title="Sterge">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog: garantie noua */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display uppercase">Garantie noua · trotinete-ieftine.ro</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">AWB intrare *</Label>
              <Input data-testid="warranty-awb-in-input" value={form.awb_in} onChange={(e) => setForm({ ...form, awb_in: e.target.value })}
                placeholder="ex: 2853100401234" className="bg-black border-neutral-800 rounded-none mt-1 font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Nume client *</Label>
                <Input data-testid="warranty-name-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Telefon client</Label>
                <Input data-testid="warranty-phone-input" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Model trotineta *</Label>
                <Select value={form.scooter_model} onValueChange={(v) => setForm({ ...form, scooter_model: v })}>
                  <SelectTrigger data-testid="warranty-model-select" className="bg-black border-neutral-800 rounded-none mt-1"><SelectValue placeholder="Alege model" /></SelectTrigger>
                  <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                    {models.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                    <SelectItem value="Alt model">Alt model</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Serie / SN</Label>
                <Input data-testid="warranty-sn-input" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1 font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Problema reclamata *</Label>
              <Textarea data-testid="warranty-issue-input" value={form.issue_description} onChange={(e) => setForm({ ...form, issue_description: e.target.value })}
                rows={3} className="bg-black border-neutral-800 rounded-none mt-1" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox data-testid="warranty-sms-checkbox" checked={form.notify_sms} onCheckedChange={(v) => setForm({ ...form, notify_sms: !!v })} />
              <span className="text-sm text-neutral-300">Trimite SMS clientului ca trotineta a ajuns in service</span>
            </label>
            <DialogFooter>
              <Button data-testid="warranty-save-btn" type="submit" className="bg-[#00E5FF] text-black hover:bg-[#5CEEFF] rounded-none uppercase tracking-wider font-bold w-full">
                Inregistreaza garantia
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: verdict */}
      <Dialog open={!!verdictTarget} onOpenChange={(v) => !v && setVerdictTarget(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display uppercase">Verdict garantie · {verdictTarget?.warranty_number}</DialogTitle></DialogHeader>
          <form onSubmit={saveVerdict} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button type="button" data-testid="verdict-repaired-btn" onClick={() => setVerdict({ ...verdict, type: "repaired" })}
                className={`border p-4 text-left transition-colors ${verdict.type === "repaired" ? "border-green-500 bg-green-950/40" : "border-neutral-800 hover:border-neutral-600"}`}>
                <ShieldCheck className="w-6 h-6 text-green-400 mb-2" />
                <div className="font-bold text-sm uppercase">Acceptata</div>
                <div className="text-xs text-neutral-400 mt-1">Reparata pe garantie</div>
              </button>
              <button type="button" data-testid="verdict-cancelled-btn" onClick={() => setVerdict({ ...verdict, type: "cancelled" })}
                className={`border p-4 text-left transition-colors ${verdict.type === "cancelled" ? "border-red-500 bg-red-950/40" : "border-neutral-800 hover:border-neutral-600"}`}>
                <ShieldX className="w-6 h-6 text-red-400 mb-2" />
                <div className="font-bold text-sm uppercase">Anulata</div>
                <div className="text-xs text-neutral-400 mt-1">Garantia NU se aplica</div>
              </button>
            </div>
            {verdict.type === "cancelled" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-red-400">Motiv anulare (obligatoriu) *</Label>
                <Select value={verdict.cancel_reason} onValueChange={(v) => setVerdict({ ...verdict, cancel_reason: v })}>
                  <SelectTrigger data-testid="cancel-reason-select" className="bg-black border-red-900 rounded-none mt-1"><SelectValue placeholder="Alege motivul" /></SelectTrigger>
                  <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                    {CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Constatare tehnica (ce ai gasit)</Label>
              <Textarea data-testid="verdict-notes-input" value={verdict.verdict_notes} onChange={(e) => setVerdict({ ...verdict, verdict_notes: e.target.value })}
                rows={3} placeholder="ex: Controller ars din cauza infiltrarii apei..." className="bg-black border-neutral-800 rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Rezolvare / Lucrari efectuate (apare pe foaia din colet)</Label>
              <Textarea data-testid="verdict-resolution-input" value={verdict.resolution} onChange={(e) => setVerdict({ ...verdict, resolution: e.target.value })}
                rows={3} placeholder="ex: Inlocuit controller, verificat instalatia electrica, testat 5 km..." className="bg-black border-neutral-800 rounded-none mt-1" />
            </div>
            <DialogFooter>
              <Button data-testid="verdict-save-btn" type="submit" className={`rounded-none uppercase tracking-wider font-bold w-full text-black ${verdict.type === "cancelled" ? "bg-red-500 hover:bg-red-400" : "bg-green-500 hover:bg-green-400"}`}>
                Salveaza verdictul
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: expediere */}
      <Dialog open={!!shipTarget} onOpenChange={(v) => !v && setShipTarget(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Expediere retur · {shipTarget?.warranty_number}</DialogTitle></DialogHeader>
          <form onSubmit={saveShip} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">AWB retur (cu care pleaca) *</Label>
              <Input data-testid="warranty-awb-out-input" value={awbOut} onChange={(e) => setAwbOut(e.target.value)}
                placeholder="ex: 2853100405678" className="bg-black border-neutral-800 rounded-none mt-1 font-mono" autoFocus />
            </div>
            <DialogFooter>
              <Button data-testid="ship-save-btn" type="submit" className="bg-purple-500 text-black hover:bg-purple-400 rounded-none uppercase tracking-wider font-bold w-full">
                <Truck className="w-4 h-4 mr-2" /> Marcheaza ca expediata
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
